import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { FilterEditor, filterKey, filterSummary, savedFilter, useGenres } from "@/components/FilterEditor";
import { useProviders } from "@/components/WatchFilter";

const LABEL = { default: "Default", mood: "Current mood" };

function ActiveSwitch({ active, onChange, busy }) {
  return (
    <div className="rounded-3xl border bg-card p-5 mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="active-profile-card">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">Currently using</p>
        <p className="font-display text-xl font-black mt-1" data-testid="active-profile-label">{LABEL[active]}</p>
        <p className="text-xs text-muted-foreground mt-1">Applies to Discover and your Watchlist. Switching shows that filter below.</p>
      </div>
      <div className="flex rounded-full bg-secondary p-1">
        {["default", "mood"].map((p) => (
          <button key={p} onClick={() => onChange(p)} disabled={busy} data-testid={`active-profile-${p}`}
            className={`h-10 px-5 rounded-full text-sm font-bold transition-colors ${active === p ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
            {LABEL[p]}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function GenresEdit() {
  const { user, setUser } = useAuth();
  const genres = useGenres();
  useProviders();
  const active = user.active_profile || "default";
  const saved = savedFilter(user, active);
  const [draft, setDraft] = useState(saved);
  const [busy, setBusy] = useState(false);
  const dirty = filterKey(draft) !== filterKey(saved);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setDraft(savedFilter(user, active)); }, [active]);

  const run = async (fn, msg) => {
    setBusy(true);
    try {
      const { data } = await fn();
      setUser(data);
      toast.success(msg);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  const save = () => run(() => api.put("/me/filters", { profile: active, ...draft }), `${LABEL[active]} saved`);
  const switchTo = (p) => {
    if (p === active) return;
    if (dirty) { toast.error(`Save or discard your ${LABEL[active].toLowerCase()} changes first`); return; }
    run(() => api.put("/me/active-profile", { profile: p }), `Now using ${LABEL[p]}`);
  };

  return (
    <div className="max-w-3xl mx-auto">
      <PageHeader eyebrow="Preferences" title="Genre preferences"
        sub="Default is what you normally like. Current mood is a temporary filter for tonight. Only the one in use is shown here." />
      <ActiveSwitch active={active} onChange={switchTo} busy={busy} />
      <div data-testid={`prefs-editor-${active}`}>
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-6">
          <h2 className="font-display text-2xl font-black" data-testid="prefs-editing-label">Editing: {LABEL[active]}</h2>
          {dirty && <span className="text-xs text-amber-600 dark:text-amber-400 font-semibold" data-testid={`prefs-unsaved-${active}`}>Unsaved changes</span>}
        </div>
        <p className="text-sm text-muted-foreground mb-8" data-testid={`prefs-saved-summary-${active}`}>
          Saved: <span className="text-foreground font-semibold">{filterSummary(saved, genres)}</span>
        </p>
        <FilterEditor prefix={active} value={draft} onChange={setDraft} />
        <div className="sticky bottom-0 mt-8 py-4 bg-background/85 backdrop-blur-xl flex flex-wrap items-center gap-3 border-t">
          <Button onClick={save} disabled={busy || !dirty} className="rounded-full h-11 px-6 font-bold" data-testid={`prefs-save-${active}`}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save {LABEL[active].toLowerCase()}
          </Button>
          {dirty && (
            <Button variant="ghost" onClick={() => setDraft(saved)} className="rounded-full h-11" data-testid={`prefs-discard-${active}`}>
              Discard changes
            </Button>
          )}
          {active === "mood" && (
            <Button variant="ghost" onClick={() => setDraft(savedFilter(user, "default"))} className="rounded-full h-11" data-testid="prefs-copy-default">
              Copy from default
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
